import React, {Dispatch, PropsWithChildren, SetStateAction, useEffect, useRef, useState} from 'react'
import DialogPortal from './DialogPortal'
import {
    XMarkIcon
} from '@heroicons/react/24/outline'
import { classNames } from '../wrapper'

interface DialogProps {
    title: React.ReactNode;
    body?: React.ReactNode;
    show: boolean;
    setShow?: Dispatch<SetStateAction<boolean>>
    // forward from DialogPortal
    mounted?: (el: HTMLDivElement) => void;
 
    // close on outside click or escape key. default true
    closeOnOutside?: boolean;
}

interface DialogInstance {
    id: number;
    visible: boolean;
}

let id: number = 0
let dialogs: DialogInstance[] = []


const Dialog: React.FC<PropsWithChildren<DialogProps>> = (props) => {
    const overlayRef = useRef<HTMLInputElement>(null)
    const [overlay, setOverlay] = useState(!props.show)
    const [mouseDown, setMouseDown] = useState(false)
    const [diff, setDiff] = useState({
        x: 0,
        y: 0
    })
    const [position, setPosition] = useState({
        x: 0,
        y: 0
    })

    const instance = useRef<DialogInstance>({
        visible: props.show,
        id: id++
    })

    function moveTo (dialog: HTMLElement, position: { x: number; y: number }) {
        setPosition({
            x: position.x,
            y: position.y
        })
        dialog.style.left = position.x + 'px'
        dialog.style.top = position.y + 'px'
        dialog.style.position = 'fixed'
    }
    function hide () {
        if (props.setShow) {
            props.setShow(false)
            setOverlay(true)
        }
    }

    useEffect(() => {
        instance.current.visible = props.show
        !props.show ? setOverlay(true) : null
    }, [props.show])

    function keydown (e: KeyboardEvent) {
        if (props.closeOnOutside === false) return;
        
        if (e.key === 'Escape' && dialogs.length > 0) {
            const visible = dialogs.reverse().find(d => d.visible)
            // we are top dialog. Only close this one
            if (visible === instance.current) {
                hide()
            }
        }
    }

    function click (e: MouseEvent) {
        if (props.closeOnOutside === false) return;
        
        if (overlayRef.current === e.target) {
            hide()
        }
    }


    useEffect(() => {
        dialogs.push(instance.current)
        window.addEventListener('click', click)
        window.addEventListener('keydown', keydown)

        return () => {
            window.removeEventListener('click', click)
            window.removeEventListener('keydown', keydown)

            // remove dialog instance.
            dialogs = dialogs.filter(d => d != instance.current)
        }
    }, [])

    return (
        props.show
            ? <DialogPortal mounted={props.mounted}>
                <div ref={overlayRef}
                     onMouseMove={e =>{
                         if (e.buttons !== 1 || !mouseDown) return

                         const dialog = document.getElementById('dialogDraggable')
                         if (!dialog) return

                         moveTo(dialog, {
                             x: e.clientX - diff.x,
                             y: e.clientY - diff.y
                         })
                         setOverlay(false)
                     }}
                     className={classNames('absolute inset-0 flex justify-center items-center z-40', overlay ? 'overlay bg-orange-600' : 'overlayClear')}>
                    <div
                        id="dialogDraggable"
                        className="max-w-full animate-modal z-50">
                        <div
                            onMouseDown={e => {
                                const dialog = document.getElementById('dialogDraggable')
                                if (!dialog) return
                                let rect = dialog.getBoundingClientRect()
                                const x = e.clientX - rect.left
                                const y = e.clientY - rect.top

                                setMouseDown(true)
                                setDiff({x, y})
                            }}

                            onMouseUp={() => {
                                setMouseDown(false)
                            }}
                            className={classNames("flex w-full items-center bg-primary-500 rounded-t-md text-white p-2 cursor-pointer", overlay ? '' : 'border-x-2 border-t-2 border-gray-400 border-opacity-50')}>
                            <div>{props.title}</div>
                            {props.setShow ?
                                <div className="ml-auto cursor-pointer" onClick={(e) => {
                                hide()
                            }}>
                                <XMarkIcon className="ml-2 h-4"/>
                            </div> : null}
                        </div>

                        <div className={classNames("bg-white h-full overflow-scroll", overlay ? '' : 'border-x-2 border-b-2 border-gray-400 border-opacity-50')} style={{ maxHeight: '70vh' }}>
                            {props.body ?? props.children}
                        </div>
                    </div>
                </div>
            </DialogPortal>
            : null
    )
}

export default Dialog
